import { Upload } from 'react-feather';
import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router';
import { BreadCrumbs, Loader, MakeToast } from '../../../../../components';
import StyledTooltip from '../../../../../components/tooltip';
import FolderTile from '../../components/folder-tiles';
import { UploadKBFolder } from '../../components';
import { truncateData } from '../../../../../services';
import { usePageHeader } from '../../../user';
import { AwsS3Icon, GoogleDriveIcon } from '../../../../../assets/images';
import {
  fileForParticularFolder,
  getKbAwsCredentials,
  getCredential,
  getKbAwsFoldersContentById,
  getKbFolders,
  getKbGDriveFoldersContentById,
} from '../../services/kb';
import FolderSpecificModal from '../../components/folder-specific-modal';
import { FolderListAction, FolderListReducerProps, TreeNode } from '../../kb.props';
import { FolderListReducer, initialStateFolderListReducer } from '../../kb.reducer';
import { uploadText, uploadTooltipText } from '../../data/constants';

const alreadyFetchedCredentials: Record<string, boolean> = {};

function FolderDetailsCard() {
  const { folderName } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(location.search);
  const service = searchParams.get('service');
  const initialFolderId = searchParams.get('folderId');
  const owner = location.state?.owner || false;
  const syncStatus = location.state?.syncStatus || 'completed';
  const [state, dispatch] = React.useReducer<
    React.Reducer<FolderListReducerProps, FolderListAction>
  >(FolderListReducer, initialStateFolderListReducer);
  const [loading, setLoading] = useState<boolean>(true);
  const [folders, setFolders] = useState<TreeNode[]>([]);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [modalData, setModalData] = useState<TreeNode[]>([]);
  const [modalLoading, setModalLoading] = useState<boolean>(false);
  const [driveUploadOverlayActive, setDriveUploadOverlayActive] = useState<boolean>(false);
  const driveUploadOverlayBaselineRef = React.useRef<number>(0);
  const driveUploadOverlayStableSuccessPollsRef = React.useRef<number>(0);
  const driveUploadOverlayVerifyInFlightRef = React.useRef<boolean>(false);

  const getTilesIcon = () => {
    switch (service) {
      case 'G-drive':
        return GoogleDriveIcon;
      default:
        return AwsS3Icon;
    }
  };

  useEffect(() => {
    setLoading(true);
    getKbFolders(initialFolderId!)
      .then((response) => {
        const fetchedFolders = response.data?.data || [];
        setFolders(fetchedFolders);
      })
      .catch((error) => {
        const err = error as { response?: { data?: { message?: string } }; message?: string };
        const msg =
          err?.response?.data?.message ||
          err?.message ||
          'Something went wrong while creating session!';
        MakeToast('error', msg);
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  const handleFolderSelected = async (
    childFolderId: number | string,
    bucketName: string,
    prefix: string,
  ) => {
    setModalLoading(true);
    setIsOpen(true);
    try {
      let fetchedChildren: TreeNode[] = [];
      let response;
      if (service === 'G-drive') {
        response = await getKbGDriveFoldersContentById(initialFolderId!, childFolderId);
      } else if (service === 'aws') {
        const payload = { prefix, bucket_name: bucketName };
        response = await getKbAwsFoldersContentById(initialFolderId!, payload);
      } else {
        setModalLoading(false);
        return;
      }
      const childFolders: TreeNode[] = response?.data?.data?.folders || [];
      const childFiles: TreeNode[] = response?.data?.data?.files || [];
      fetchedChildren = [...childFolders, ...childFiles];
      setModalData(fetchedChildren);
    } catch (error) {
      let message = 'An error occurred';
      if (typeof error === 'string') {
        message = error;
      } else if (error instanceof Error && error.message) {
        message = error.message;
      }
      MakeToast('error', message);
    } finally {
      setModalLoading(false);
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const shouldOpen = params.get('uploadFolder') === 'true';
    const folderId = params.get('folderId');
    const services = params.get('service') ?? '';
    if (services !== 'system') {
      const fetchKey = `${folderId}-${services}`;
      if (!shouldOpen || !folderId || !services || alreadyFetchedCredentials[fetchKey]) return;
      alreadyFetchedCredentials[fetchKey] = true;
      dispatch({ type: 'TOGGLE_UPLOAD_MODAL', payload: true });
      dispatch({ type: 'SET_SERVICE', payload: services });
      getCredential(services === 'G-drive' ? 'google' : 'aws', folderId)
        .then((res) => {
          dispatch({ type: 'SET_CREDENTIALS', payload: res.data.data || [] });
          dispatch({ type: 'SET_REDIRECT', payload: res.data?.data?.redirect || false });
        })
        .catch((error) => {
          const err = error as { response?: { data?: { message?: string } }; message?: string };
          const msg = err?.response?.data?.message || err?.message || 'An error occured';
          MakeToast('error', msg);
          dispatch({ type: 'SET_CREDENTIALS', payload: [] });
        });
    }
  }, [location.search]);

  const handleUploadClick = () => {
    const searchParam = new URLSearchParams(location.search);
    searchParam.set('uploadFolder', 'true');
    searchParam.set('folderId', String(initialFolderId));
    searchParam.set('service', service ?? '');
    dispatch({ type: 'TOGGLE_UPLOAD_MODAL', payload: true });
    dispatch({ type: 'SET_SERVICE', payload: service ?? '' });
    dispatch({ type: 'FOLDER_ID', payload: Number(initialFolderId) });
    navigate(`${location.pathname}?${searchParam.toString()}`, { replace: true });
  };

  const handleCancelUploadClick = () => {
    const params = new URLSearchParams(location.search);
    const folderId = params.get('folderId');
    const services = params.get('service');
    if (folderId && services) {
      const fetchKey = `${folderId}-${services}`;
      delete alreadyFetchedCredentials[fetchKey];
    }
    dispatch({ type: 'TOGGLE_UPLOAD_MODAL', payload: false });
    dispatch({ type: 'SET_REDIRECT', payload: false });
    params.delete('uploadFolder');
    params.delete('folderId');
    params.delete('service');
    navigate(`/knowledge-base`, { replace: true });
  };

  const getFolderFilesPayload = () => ({
    limit: 10,
    offset: 0,
    search: '',
    sort_col: 'created_at',
    sort_order: 'DESC' as const,
    filters: [{ type: 'file_type', value: [] as string[] }],
  });

  const getCurrentFolderFilesCount = async (folderId: number) => {
    try {
      const res = await fileForParticularFolder(getFolderFilesPayload(), folderId);
      return Number(res?.data?.data?.total_files ?? 0);
    } catch {
      return 0;
    }
  };

  const resetDriveOverlayState = () => {
    driveUploadOverlayBaselineRef.current = 0;
    driveUploadOverlayStableSuccessPollsRef.current = 0;
    driveUploadOverlayVerifyInFlightRef.current = false;
    setDriveUploadOverlayActive(false);
  };

  const verifyDriveUploadSynced = async (folderId: number, baseline: number) => {
    if (driveUploadOverlayVerifyInFlightRef.current) return false;
    driveUploadOverlayVerifyInFlightRef.current = true;
    try {
      const latestCount = await getCurrentFolderFilesCount(folderId);
      return latestCount > baseline;
    } finally {
      driveUploadOverlayVerifyInFlightRef.current = false;
    }
  };

  const handleDriveSyncStarted = (folderId: number) => {
    driveUploadOverlayStableSuccessPollsRef.current = 0;
    driveUploadOverlayVerifyInFlightRef.current = false;
    setDriveUploadOverlayActive(true);
    getCurrentFolderFilesCount(folderId).then((count) => {
      driveUploadOverlayBaselineRef.current = count;
    });
  };

  const handleDriveSyncFailed = () => {
    resetDriveOverlayState();
  };

  useEffect(() => {
    const normalizedFolderId = Number(initialFolderId);
    if (!driveUploadOverlayActive || !normalizedFolderId) return undefined;
    const interval = setInterval(() => {
      getKbAwsCredentials([normalizedFolderId])
        .then((res) => {
          if (res.status !== 200) return;
          const rows = res.data?.data || [];
          const row = rows.find((item: { id: number }) => item.id === normalizedFolderId);
          const baseline = driveUploadOverlayBaselineRef.current;
          if (row?.status === 'error') {
            resetDriveOverlayState();
            return;
          }
          if (row?.status === 'inprogress') {
            driveUploadOverlayStableSuccessPollsRef.current = 0;
            return;
          }
          verifyDriveUploadSynced(normalizedFolderId, baseline).then((isSynced) => {
            if (isSynced) {
              resetDriveOverlayState();
            }
          });
          if (row?.status === 'success') {
            driveUploadOverlayStableSuccessPollsRef.current += 1;
          } else {
            driveUploadOverlayStableSuccessPollsRef.current = 0;
          }
          // Empty selection fallback: keep behavior aligned with KB listing page.
          if (driveUploadOverlayStableSuccessPollsRef.current >= 12) {
            resetDriveOverlayState();
          }
        })
        .catch(() => {
          MakeToast('error', 'Failed to fetch upload status.');
        });
    }, 5000);
    return () => clearInterval(interval);
  }, [driveUploadOverlayActive, initialFolderId]);

  const headerLeftContent = useMemo(
    () => (
      <div className="flex flex-col gap-1 w-full">
        <div className="table-name">
          <StyledTooltip title={folderName}>
            <p className="font-bold text-2xl text-[hsl(var(--foreground))]">
              {truncateData(folderName ?? '', 40)}
            </p>
          </StyledTooltip>
        </div>
        <div className="breadcrumbs">
          <BreadCrumbs />
        </div>
      </div>
    ),
    [],
  );

  const isSyncInProgress = syncStatus === 'inprogress' || driveUploadOverlayActive;

  usePageHeader({ headerLeftContent });

  return (
    <>
      {loading ? (
        <div className="flex items-center justify-center h-full min-h-[300px]">
          <Loader height="10px" width="10px" />
        </div>
      ) : (
        <div className="relative">
          <div className="mb-4 flex items-center justify-between">
            <div className="flex items-center gap-[0.625rem]">
              <span
                className={`${service === 'G-drive' ? 'bg-green-100' : 'bg-orange-100'} rounded-full flex items-center justify-center w-10 h-10`}
              >
                <img src={getTilesIcon()} alt={service?.toString()} width={25} height={25} />
              </span>
              <span className="font-bold text-base leading-3 ">
                Source: {service === 'G-drive' ? 'Google Drive' : 'Amazon S3'}
              </span>
            </div>
            {}
            {owner &&
              (isSyncInProgress ? (
                <StyledTooltip title={uploadTooltipText}>
                  <button
                    type="button"
                    aria-label="upload"
                    onClick={handleUploadClick}
                    disabled
                    className="rounded-[23.4375rem] border-[0.046875rem] px-[0.75rem] py-[0.25rem] gap-[0.375rem] text-[#fff] flex items-center bg-blue-950 opacity-50 cursor-not-allowed"
                  >
                    <Upload width={18} height={18} />
                    {uploadText}
                  </button>
                </StyledTooltip>
              ) : (
                <button
                  type="button"
                  aria-label="upload"
                  onClick={handleUploadClick}
                  className="rounded-[23.4375rem] border-[0.046875rem] px-[0.75rem] py-[0.25rem] gap-[0.375rem] text-[#fff] flex items-center bg-blue-950"
                >
                  <Upload width={18} height={18} />
                  {uploadText}
                </button>
              ))}
          </div>
          {folders.length === 0 ? (
            <p className="text-gray-500">No subfolders available in this folder.</p>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              {folders.map((folder) => (
                <FolderTile
                  key={folder.id}
                  name={folder.name}
                  onClick={() =>
                    handleFolderSelected(folder.id, folder.bucket_name!, folder.prefix!)
                  }
                />
              ))}
            </div>
          )}
          {driveUploadOverlayActive && (
            <div className="absolute inset-0 z-20 flex items-center justify-center rounded-xl bg-[hsl(var(--background))]/70 backdrop-blur-[1px]">
              <Loader height="10px" width="10px" />
            </div>
          )}
        </div>
      )}
      <FolderSpecificModal
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        isLoading={modalLoading}
        service={
          (service === 'G-drive' || service === 'aws' || service === 'system' ? service : 'aws') as
            | 'G-drive'
            | 'aws'
            | 'system'
        }
        folderId={Number(initialFolderId!)}
        data={modalData}
      />
      <UploadKBFolder
        service={state.service || 'system'}
        open={state.uploadModalOpen}
        idToUpload={Number(initialFolderId!)}
        dispatcher={dispatch}
        credentials={state.credentials}
        redirect={state.redirect}
        handleCancelUploadClick={handleCancelUploadClick}
        onDriveSyncStarted={handleDriveSyncStarted}
        onDriveSyncFailed={handleDriveSyncFailed}
      />
    </>
  );
}

export default FolderDetailsCard;
